import { useCallback, useState, type ComponentType } from "react"
import moment, { type Moment } from "moment"
import { Chip } from "@mui/material"
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material"
import PeriodSelectorOptionsUntyped from "components/period-selector/options"
import PeriodDisplay from "connected-components/dashboard/components/period-display"
import OPTooltip from "components/material/tooltip"
import { PERIOD_OPTIONS, CUSTOM_PERIOD, type Period } from "helpers/dates"
import type { IsoDate } from "helpers/types/dates"
import { computeDateOverride, formatDateRangeLabel, type DateOverride } from "../../helpers/date-override"
import "./style.scss"

// components/period-selector/options.jsx is untyped and calls endDate.format(...) unconditionally,
// so endDate must be a Moment despite its PropTypes declaring a string. Needs `as unknown as` (not
// a direct `as ComponentType` like risk/header.tsx's ListItemDropDown cast) since the inferred JS
// prop shape doesn't overlap enough with this interface for a direct cast to compile.
interface PeriodSelectorOptionsProps {
  period: string
  customizeOptions?: (options: { value: Period, label: string }[]) => { value: Period, label: string }[]
  handleClickAway: () => void
  handleSelect: (period: Period) => void
  endDate: Moment
  latestDate: string
}
const PeriodSelectorOptions = PeriodSelectorOptionsUntyped as unknown as ComponentType<PeriodSelectorOptionsProps>

// No custom date selection for MVP — presets only.
const PANEL_PERIOD_OPTIONS = PERIOD_OPTIONS.filter(({ value }) => value !== CUSTOM_PERIOD)
// Stable reference so PeriodSelectorOptions' useMemo([customizeOptions]) actually memoizes.
const getPanelPeriodOptions = () => PANEL_PERIOD_OPTIONS

interface PanelPeriodControlDates {
  earliestDate: IsoDate | null
  latestDate: IsoDate | null
  startDate: IsoDate
  endDate: IsoDate
}

interface PanelPeriodControlProps {
  dates: PanelPeriodControlDates
  dateOverride: DateOverride | null
  isDateOverrideActive: boolean
  setDateOverride: (dateOverride: DateOverride) => void
  clearDateOverride: () => void
  isPerformance?: boolean
}

// Each module (graph/components/panel-period-control.tsx, and KPI/Factors/Asset View's own
// wrappers) calls its own data hook and passes the result in, so this component stays hook-agnostic.
export default function PanelPeriodControl({
  dates: { earliestDate, latestDate, startDate, endDate },
  dateOverride,
  isDateOverrideActive,
  setDateOverride,
  clearDateOverride,
  isPerformance = true,
}: PanelPeriodControlProps) {
  const [open, setOpen] = useState(false)
  const datesReady = earliestDate != null && latestDate != null

  // earliestDate/latestDate load asynchronously, independent of whatever gates the panel's render.
  // setOpen(!open) (not a functional update) matches PeriodSelector's handleToggle — a functional
  // update fights the dropdown's ClickAwayListener, which also setOpen(false)s on the same click.
  const handleToggle = useCallback(() => {
    if (!datesReady) return
    setOpen(!open)
  }, [datesReady, open])
  const handleClickAway = useCallback(() => setOpen(false), [])

  const handleSelect = useCallback((period: Period) => {
    setOpen(false)
    const computed = computeDateOverride(period, earliestDate, latestDate)
    if (computed) setDateOverride(computed)
  }, [setDateOverride, earliestDate, latestDate])

  // latestDate is only rendered here once open is true, which handleToggle only sets when
  // datesReady (earliestDate/latestDate both non-null) — the cast reflects that runtime guarantee,
  // since PeriodSelectorOptionsProps.latestDate isn't nullable.
  const options = open && (
    <PeriodSelectorOptions
      period={dateOverride?.period ?? CUSTOM_PERIOD}
      customizeOptions={getPanelPeriodOptions}
      handleClickAway={handleClickAway}
      handleSelect={handleSelect}
      endDate={moment(latestDate)}
      latestDate={latestDate as string}
    />
  )

  if (dateOverride) {
    // isDateOverrideActive false means the stored override fell back to dashboard dates (boundaries
    // not loaded yet, or a collapsed fixed-length window) — keep the chip (and its × ) so the stored
    // override stays clearable, but don't claim it's applied when the panel is using dashboard dates.
    return (
      <div className={`components_period-selector panel-period-control panel-period-control--active${isDateOverrideActive ? "" : " panel-period-control--inactive"}`}>
        <span className="panel-period-control__label">Period</span>
        <OPTooltip
          title={isDateOverrideActive
            ? "Click to change period, or use the × to revert to dashboard dates"
            : "This period isn't applied — showing dashboard dates. Use the × to clear it."}
          placement="top"
        >
          <Chip
            className="panel-period-control__chip"
            size="small"
            // startDate/endDate (not dateOverride.startDate/endDate) — these are resolveDateOverride's
            // clamped values, so the chip can't show a wider range than what the panel actually queries.
            label={`${dateOverride.period}: ${formatDateRangeLabel(startDate, endDate)}`}
            onClick={handleToggle}
            onDelete={clearDateOverride}
          />
        </OPTooltip>
        {options}
      </div>
    )
  }

  return (
    <div className="components_period-selector panel-period-control">
      <span className="panel-period-control__label">Period</span>
      <a className="link panel-period-control__button" onClick={handleToggle}>
        <PeriodDisplay isPerformance={isPerformance} hideLabel />
        {open ? <ArrowDropUp /> : <ArrowDropDown />}
      </a>
      {options}
    </div>
  )
}
